import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { post, get, patch } from '../../../../helpers/httpClient';
import { toast } from 'react-toastify';
import PageBasicFields from "../../../../helpers/PageBasicFields";

function CatalogEditor() {
  const navigate = useNavigate();
  const { id, pageId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!pageId;

  const [form, setForm] = useState({ slug: '', title: '', extra: {} });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!pageId);

  const titleText = useMemo(() => (isEdit ? 'Edit Catalog' : 'New Catalog'), [isEdit]);

  // Components normalization moved into ComponentsCollectionEditor

  useEffect(() => {
    let isMounted = true;
    async function loadPage() {
      if (!isEdit) return;
      try {
        setLoading(true);
        setError('');
        const resp = await get(`pages/${pageId}`);
        const page = resp?.page || resp;
        if (!page) throw new Error('Not found');
        if (!isMounted) return;
        setForm({
          slug: page.slug || '',
          title: page.title || '',
          preview: page.preview,
          components: page.components || { items: [] },
          extra: page.extra || {},
        });
      } catch (e) {
        if (isMounted) toast.error('Failed to load page data.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadPage();
    return () => { isMounted = false; };
  }, [id, pageId, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.title.trim()) {
      toast.error('Title is required.');
      return;
    }

    const payload = {
      type: 'catalog',
      slug: (form.slug || '').trim(),
      title: (form.title || '').trim(),
      preview: form.preview || null,
      components: form.components || [],
      extra: form.extra || {},
    };

    try {
      setSaving(true);
      setError('');
      if (isEdit) {
        await patch(`websites/${id}/pages/${pageId}`, payload);
      } else {
        const parent = searchParams.get('parent');
        await post(`websites/${id}/pages`, { ...payload, order: 0, parent: parent || null });
      }
      navigate(`/websites/${id}?tab=structure`);
    } catch (err) {
      toast.error(isEdit ? 'Failed to update page.' : 'Failed to create page.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => navigate(-1);

  return (
    <Container className="mt-4">
      <Row>
        <Col md={{ span: 10, offset: 0 }} lg={{ span: 9, offset: 0 }}>
          <Card>
            <Card.Body>
              <div className="structure-node-header mb-3">
                <h2 className="m-0">{titleText}</h2>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              {loading ? (
                <div className="text-center my-3"><Spinner animation="border" /></div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <PageBasicFields
                    form={form}
                    setForm={setForm}
                    websiteId={id}
                    slugPlaceholder="e.g. products"
                    titlePlaceholder="Enter title"
                    preserveEmptySlug={isEdit}
                  />


                    <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="submit" className="primary-button" disabled={saving}>
                      {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
                    </Button>
                    <Button className="primary-button" onClick={handleCancel} disabled={saving}>Cancel</Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default CatalogEditor;
